import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, ShieldAlert } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

const inputCls = "w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none transition-colors focus:border-primary";

export default function ProfileSettings() {
  const { user, updateProfile, resendVerification } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [saving, setSaving] = useState(false);
  const initial = (user?.name || user?.email || "?").charAt(0).toUpperCase();

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateProfile(name.trim());
      toast.success("Profile updated.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  const resend = async () => {
    try {
      const data = await resendVerification();
      toast.success(data.message || "Verification email sent.");
    } catch {
      toast.error("Could not send email.");
    }
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-6 sm:p-8" data-testid="profile-settings">
      <div className="flex items-center gap-4">
        <Avatar className="h-16 w-16">
          {user?.picture && <AvatarImage src={user.picture} alt={user?.name} />}
          <AvatarFallback className="brand-gradient-bg text-xl font-semibold text-white">{initial}</AvatarFallback>
        </Avatar>
        <div>
          <h2 className="font-display text-xl font-semibold tracking-tight">{user?.name}</h2>
          <p className="text-sm text-muted-foreground">{user?.email}</p>
          <span className="mt-1 inline-block rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground ring-1 ring-border">
            {user?.auth_provider === "google" ? "Google account" : "Email account"}
          </span>
        </div>
      </div>

      <div className="mt-6 flex items-center gap-2 rounded-xl border border-border bg-background/50 p-4 text-sm">
        {user?.email_verified ? (
          <><CheckCircle2 className="h-4 w-4 text-brand-teal" /> Your email is verified.</>
        ) : (
          <>
            <ShieldAlert className="h-4 w-4 text-brand-orange" />
            <span>Email not verified.</span>
            <button onClick={resend} data-testid="profile-resend-btn" className="ml-auto rounded-full bg-foreground px-3 py-1.5 text-xs font-semibold text-background">Resend email</button>
          </>
        )}
      </div>

      <form onSubmit={save} className="mt-6 space-y-4" data-testid="profile-form">
        <div>
          <label className="mb-1.5 block text-sm font-medium">Full name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} data-testid="profile-name-input" required />
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-medium">Email</label>
          <input value={user?.email || ""} disabled className={`${inputCls} opacity-60`} />
        </div>
        <button type="submit" disabled={saving} data-testid="profile-save-btn" className="rounded-full bg-foreground px-5 py-2.5 text-sm font-semibold text-background transition-all hover:-translate-y-0.5 disabled:opacity-60">
          {saving ? "Saving…" : "Save changes"}
        </button>
      </form>
    </div>
  );
}
